import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tierApi } from '../services/contactsApi';
import type { TierCreate, TierUpdate } from '../types';

export const tierKeys = {
  all: ['contacts-tiers'] as const,
  list: () => [...tierKeys.all, 'list'] as const,
  detail: (id: string) => [...tierKeys.all, 'detail', id] as const,
  companies: (id: string) => [...tierKeys.all, 'companies', id] as const,
};

export function useTiers() {
  return useQuery({
    queryKey: tierKeys.list(),
    queryFn: () => tierApi.list(),
  });
}

export function useTier(id: string) {
  return useQuery({
    queryKey: tierKeys.detail(id),
    queryFn: () => tierApi.get(id),
    enabled: !!id,
  });
}

export function useCreateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TierCreate) => tierApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tierKeys.all });
    },
  });
}

export function useUpdateTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TierUpdate }) => tierApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: tierKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tierKeys.all });
    },
  });
}

export function useDeleteTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tierApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tierKeys.all });
    },
  });
}

export function useInitializeDefaultTiers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tierApi.initializeDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tierKeys.all });
    },
  });
}

export function useTierCompanies(tierId: string, params?: { search?: string; offset?: number; limit?: number }) {
  return useQuery({
    queryKey: [...tierKeys.companies(tierId), params],
    queryFn: () => tierApi.listCompanies(tierId, params),
    enabled: !!tierId,
  });
}

export function useAssignCompaniesToTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tierId, organizationIds }: { tierId: string; organizationIds: string[] }) =>
      tierApi.assignCompanies(tierId, organizationIds),
    onSuccess: (_, { tierId }) => {
      queryClient.invalidateQueries({ queryKey: tierKeys.companies(tierId) });
      queryClient.invalidateQueries({ queryKey: tierKeys.all });
      // Also invalidate companies list since tier assignments changed
      queryClient.invalidateQueries({ queryKey: ['contacts-companies'] });
    },
  });
}

export function useRemoveOrganizationFromTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tierId, organizationId }: { tierId: string; organizationId: string }) =>
      tierApi.removeCompany(tierId, organizationId),
    onSuccess: (_, { tierId }) => {
      queryClient.invalidateQueries({ queryKey: tierKeys.companies(tierId) });
      queryClient.invalidateQueries({ queryKey: tierKeys.all });
      queryClient.invalidateQueries({ queryKey: ['contacts-companies'] });
    },
  });
}
